import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import type { User, Session, SupabaseClient } from "@supabase/supabase-js";

interface Bookkeeper {
  id: string;
  email: string;
  full_name: string;
  company_name: string;
  reply_to_email: string;
  plan: "free" | "solo" | "pro" | "agency";
  created_at: string;
  brand_color: string | null;
  brand_logo_url: string | null;
  email_from_name: string | null;
  lemonsqueezy_customer_id: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  bookkeeper: Bookkeeper | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshBookkeeper: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Lazy-load the Supabase client so its ~80 KB gzip payload doesn't ship
// in the main bundle on first paint of the landing page.
let _clientPromise: Promise<SupabaseClient> | null = null;
function getClient(): Promise<SupabaseClient> {
  if (!_clientPromise) {
    _clientPromise = import("@/integrations/supabase/client").then((m) => m.supabase);
  }
  return _clientPromise;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [bookkeeper, setBookkeeper] = useState<Bookkeeper | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchBookkeeper = async (userId: string) => {
    const supabase = await getClient();
    const { data } = await supabase
      .from("bookkeepers")
      .select("*")
      .eq("id", userId)
      .single();
    setBookkeeper(data as Bookkeeper | null);
  };

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let cancelled = false;

    const boot = async () => {
      const supabase = await getClient();
      if (cancelled) return;

      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        async (_event, session) => {
          setSession(session);
          setUser(session?.user ?? null);
          if (session?.user) {
            setTimeout(() => fetchBookkeeper(session.user.id), 0);
          } else {
            setBookkeeper(null);
          }
          setLoading(false);
        }
      );
      unsubscribe = () => subscription.unsubscribe();

      const { data: { session } } = await supabase.auth.getSession();
      if (cancelled) return;
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchBookkeeper(session.user.id);
      }
      setLoading(false);
    };

    // Defer the Supabase client download/parse until the browser is idle so
    // it doesn't compete with LCP-critical resources on landing-page loads.
    // Falls back to a short timeout in browsers without requestIdleCallback.
    let idleHandle: number | undefined;
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
    const ric = (window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    }).requestIdleCallback;
    if (typeof ric === "function") {
      idleHandle = ric(() => { void boot(); }, { timeout: 1500 });
    } else {
      timeoutHandle = setTimeout(() => { void boot(); }, 200);
    }

    return () => {
      cancelled = true;
      if (idleHandle !== undefined) {
        const cic = (window as unknown as {
          cancelIdleCallback?: (h: number) => void;
        }).cancelIdleCallback;
        cic?.(idleHandle);
      }
      if (timeoutHandle) clearTimeout(timeoutHandle);
      unsubscribe?.();
    };
  }, []);

  const signOut = async () => {
    const supabase = await getClient();
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setBookkeeper(null);
  };

  const refreshBookkeeper = async () => {
    if (user) await fetchBookkeeper(user.id);
  };

  return (
    <AuthContext.Provider value={{ user, session, bookkeeper, loading, signOut, refreshBookkeeper }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
